import { useTimeline } from '@src/features/absence';
import {
  doesTimedRangeOverlapAbsences,
  getUserAbsences,
} from '@src/features/absence/lib';
import { useFormContext } from '@src/shared/form';
import { TimeSelectField } from '@src/shared/ui/fields';
import type { AutocompleteOption } from '@src/shared/ui/inputs';
import { Stack } from '@src/shared/ui/layout';
import { type ComponentPropsWithoutRef, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { ScheduleFormValues } from '../Form';

const StyleSheet = {
  field: {
    '&.MuiAutocomplete-root .MuiInputBase-root.MuiAutocomplete-inputRoot': {
      paddingRight: '50px',
    },
  },
};
type TimeSelectFieldProps = ComponentPropsWithoutRef<typeof TimeSelectField>;

function TimeFromField(props: TimeSelectFieldProps) {
  const { t } = useTranslation();

  return (
    <TimeSelectField
      label={t('absence.schedule.field_time_from', 'From')}
      name="startTime"
      sx={StyleSheet.field}
      {...props}
    />
  );
}
function TimeToField(props: TimeSelectFieldProps) {
  const { t } = useTranslation();
  return (
    <TimeSelectField
      label={t('absence.schedule.field_time_to', 'To')}
      name="endTime"
      sx={StyleSheet.field}
      {...props}
    />
  );
}

type TimeRangeFieldProps = {
  disabled?: boolean;
  /** Hard bounds (withdrawal): the range may only be narrowed within these. */
  minTime?: string;
  maxTime?: string;
};

export default function TimeRangeField({
  disabled = false,
  minTime,
  maxTime,
}: TimeRangeFieldProps) {
  const { watch } = useFormContext() as UseFormReturn<ScheduleFormValues>;
  const { absences, selectedSlot } = useTimeline();
  const [userId, dateFrom, startTime, endTime] = watch([
    'userId',
    'dateFrom',
    'startTime',
    'endTime',
  ]);

  const userAbsences = useMemo(
    () => getUserAbsences(userId ?? null, absences),
    [absences, userId],
  );

  // Every row the edit is holding, not just the one the slot names singly: a
  // multi-day block carries its days in `editedAbsenceIds` and leaves
  // `editedAbsenceId` null. Reading only the latter let the block count as
  // occupying its own day -- an all-day absence overlaps any hours asked for --
  // and disabled every option in the second field once the first was chosen.
  const editedAbsenceIds = selectedSlot?.editedAbsenceIds;
  const editedAbsenceId = selectedSlot?.editedAbsenceId;
  const excludeIds = useMemo(
    () => editedAbsenceIds ?? (editedAbsenceId ? [editedAbsenceId] : []),
    [editedAbsenceIds, editedAbsenceId],
  );

  const isOutOfBounds = (value: string) =>
    (!!minTime && value < minTime) || (!!maxTime && value > maxTime);

  const isOccupied = (
    nextStartTime: string | null,
    nextEndTime: string | null,
  ) => {
    if (!dateFrom || !userId || !nextStartTime || !nextEndTime) {
      return false;
    }

    return doesTimedRangeOverlapAbsences(
      userAbsences,
      dateFrom,
      nextStartTime,
      nextEndTime,
      excludeIds,
    );
  };

  return (
    <Stack spacing={2} direction="row">
      <TimeFromField
        disabled={disabled}
        minutesStep={15}
        required
        getOptionDisabled={(option: AutocompleteOption) => {
          const value = String(option.value);

          return (
            isOutOfBounds(value) ||
            (!!endTime && value >= endTime) ||
            isOccupied(value, endTime ?? null)
          );
        }}
      />
      <TimeToField
        disabled={disabled}
        minutesStep={15}
        required
        getOptionDisabled={(option: AutocompleteOption) => {
          const value = String(option.value);

          return (
            isOutOfBounds(value) ||
            (!!startTime && value <= startTime) ||
            isOccupied(startTime ?? null, value)
          );
        }}
      />
    </Stack>
  );
}
